<template>
	<widget title="Job Defaults" icon="fa-file">

		<p>You can set up default field settings for projects here. These can be over-ridden on individual projects.</p>

		<vue-form 
			v-if="'CompanySetting' in $store.company"
			class="p-2"
			:no-top-button="true" 
			:working="working"
			v-on:submit="save">

			<h4>Default Roles</h4>

			<p>Assign users to a default role when you create new projects and services (max 2).</p>

			<form-group label="Projects"
				col-class="col-auto">

				<div v-for="(projectRole,index) in settings.default_users_roles.projects" class="d-flex align-items-center">
					<field label="Employee"
						col-class="col-auto"
						class="mr-2 mb-0"
						:params="{
							type: 'select',
							options: userOptions()
						}"
						v-model="settings.default_users_roles.projects[index].user_id">
					</field>
					<field label="Role"
						col-class="col-auto"
						class="mr-2 mb-0"
						:params="{
							type: 'select',
							options: rolesOptions
						}"
						v-model="settings.default_users_roles.projects[index].role_id">
					</field>
					<i class="icon-button fa fa-trash text-danger" v-on:click="removeDefaultRole('projects',index)"></i>
				</div>

				<i v-if="settings.default_users_roles.projects.length < 2" class="fa fa-plus icon-button text-success mt-2" v-on:click="addDefaultRole('projects')"></i>

			</form-group>

			<form-group label="Services"
				col-class="col-auto">

				<div v-for="(projectRole,index) in settings.default_users_roles.services" class="d-flex align-items-center">
					<field label="Employee"
						col-class="col-auto"
						class="mr-2 mb-0"
						:params="{
							type: 'select',
							options: userOptions()
						}"
						v-model="settings.default_users_roles.services[index].user_id">
					</field>
					<field label="Role"
						col-class="col-auto"
						class="mr-2 mb-0"
						:params="{
							type: 'select',
							options: rolesOptions
						}"
						v-model="settings.default_users_roles.services[index].role_id">
					</field>
					<i class="icon-button fa fa-trash text-danger" v-on:click="removeDefaultRole('services',index)"></i>
				</div>

				<i v-if="settings.default_users_roles.services.length < 2" class="fa fa-plus icon-button text-success mt-2" v-on:click="addDefaultRole('services')"></i>

			</form-group>

			<hr>

			<field v-if="$store.payBoostOptions.length > 0"
				label="Pay Adjustments for Time Log" 
				col-class="col-xl-3 col-md-4 col-sm-6" 
				v-model="settings.payboost_ids" 
				:params="{type: 'select-multi', options: $store.payBoostOptions}">	
			</field>
			<div v-else class="d-flex align-items-center mb-2">
				Pay Adustments not set up yet. <router-link v-if="hasModulePermWrite('Settings') && hasModulePermWrite('Users')" to="/employees/pay-adjustments">Add some</router-link>
			</div>		

			<field :label="checkinToleranceLabel" 
				col-class="col-xl-3 col-md-4 col-sm-6" 
				v-model="settings.check_in_tolerance" 
				:params="{type: 'select', options: checkinToleranceOptions}">		
			</field>

			<template v-for="(union,index) in $store.unions" v-if="$store.projectUnionCollectiveAgreements">

				<field :label="'Compensation Schedule for '+union.union_name" 
					col-class="col-xl-3 col-md-4 col-sm-6" 
					:params="{type: 'select', options: collectiveAgreementOptions(union.id)}" 
					v-model="$store.projectUnionCollectiveAgreements[union.id]">
				</field>

				<field
					v-if="$store.projectUnionCollectiveAgreements[union.id] && mileageZoneOptions(union.id, $store.projectUnionCollectiveAgreements[union.id]).length > 0"
					:label="'Mileage Zone for '+union.union_name"
					col-class="col-xl-3 col-md-4 col-sm-6"
					:params="{type:'select',options:mileageZoneOptions(union.id, $store.projectUnionCollectiveAgreements[union.id])}"
					v-model="$store.projectMileageZones[union.id]">
					<hr>
				</field>

			</template>

			<field label="Mileage Pay" 
				col-class="col-xl-3 col-md-4 col-sm-6" 
				v-model="settings.pays_mileage" 
				:params="{type: 'boolean'}">		
			</field>

			<field label="Per Diem Pay" 
				col-class="col-xl-3 col-md-4 col-sm-6" 
				v-model="settings.pays_perdiem" 
				:params="{type: 'boolean'}">
			</field>

			<field label="Read Restricted" col-class="stacked"
				:params="{type:'boolean'}"
				v-model="settings.read_restricted">
				<p class="text-muted">
					If checked, only personnel specifically assigned to a role with read permissions enabled in this project can read this project data.
				</p>
			</field>

			<field label="Write Restricted" col-class="stacked"
				:params="{type:'boolean'}"
				v-model="settings.write_restricted">
				<p class="text-muted">
					If checked, only personnel specifically assigned to a role with write permissions enabled in this project can write this project data.
				</p>
			</field>

			<hr>

			<h3>Service Defaults</h3>

			<p>You can set up default field settings for services here. These can be over-ridden on individual services.</p>

			<field label="Disable Work To Do Signatures"
				 col-class="col-xl-3 col-md-4 col-sm-6"
				 v-model="settings.work_to_do_disabled"
				 :params="{type: 'boolean'}">
			</field>

			<field label="Disable Work Order Signatures"
				 col-class="col-xl-3 col-md-4 col-sm-6"
				 v-model="settings.work_orders_disabled"
				 :params="{type: 'boolean'}">
			</field>

			<field label="Minimum Flat Rate ($)"
				 col-class="col-xl-3 col-md-4 col-sm-6"
				 v-model="settings.min_rate"
				 :params="{type: 'number', min:0, step:0.01}">
			</field>

			<hr>

			<div>
				<!--<h3>Default Display Options</h3>-->

				<invoice-display-settings
					:global-settings="settings"
					layer="global"
					v-on:settingsChange="invoiceDisplaySettingsChange">	
				</invoice-display-settings>

			</div>



		</vue-form>

	</widget>
</template>
<script type="text/javascript">
import Widget from "@/components/Widget"
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import VueForm from "@/components/VueForm"

import JobMixins from "@/mixins/JobMixins.js"

import InvoiceDisplaySettings from "@/components/InvoiceDisplaySettings";
import InvoiceEquipmentSection from "../../pages/project/services/invoicing/InvoiceEquipmentSection";

export default {
	name: "ProjectDefaultSettings",
	components: {
		InvoiceEquipmentSection,
		Widget,
		Field,
		VueForm,
		FormGroup,
		InvoiceDisplaySettings
	},
	mixins: [JobMixins],
	data() {
		return {
			settings: {
				write_restricted: false,
				read_restricted: false,
				pays_perdiem: false,
				pays_mileage: false,
				check_in_tolerance: 200,
				work_to_do_disabled: false,
				work_orders_disabled: false,
				min_rate: 0,
				payboost_ids: [],
				default_users_roles: []
			},
			working: false
		} 
	},
	watch: {
		'$store.company'() {
			this.setSettings()
		}
	},
	computed: {
		rolesOptions() {
			return this.optionify(this.$store.jobPersonnelRoles,"title")
		},
	},
	methods: {

		invoiceDisplaySettingsChange(settings) {
			/* Handles changes to invoice display settings */

			Object.keys(settings).forEach(setting => {
				this.settings[setting] = settings[setting]
			})
		},

		setSettings() {
			this.settings = {}

			try {
				this.settings = JSON.parse(this.$store.company.CompanySetting.job_defaults_json)
			}
			catch (e) {}

			this.$store.projectUnionCollectiveAgreements = this.settings.collective_agreements

			if (!("default_users_roles" in this.settings)) {
				this.settings.default_users_roles = {
					projects: [],
					services: []
				}
			}

		},
		addDefaultRole(jobType) {
			this.settings.default_users_roles[jobType].push({
				user_id: 0,
				role_id: 0
			})
			this.$forceUpdate()
		},
		removeDefaultRole(jobType,index) {
			this.settings.default_users_roles[jobType].splice(index,1)
			this.$forceUpdate()
		},
		save() {
			let vm = this

			this.working = true

			this.settings.collective_agreements = this.$store.projectUnionCollectiveAgreements
			this.settings.mileage_zones = this.$store.projectMileageZones

			if ('min_rate' in this.settings)
				this.settings.min_rate = parseFloat(this.settings.min_rate).toFixed(2)

			let payload = {
				job_defaults_json: JSON.stringify(this.settings)
			}

			this.postData("CompanySetting/save",payload,true).then(response => {

				vm.working = false

				if (response.success) {
					vm.$store.company.CompanySetting.job_defaults_json = payload.job_defaults_json
					vm.alertSaved("Project Default Settings Saved")
				}

			})
		},
		fetchData() {
			let vm = this

			this.spinner(true)

			let promises = [
				this.getJSON("PayBoost/get"),
				this.fetchUnions(),
				this.fetchJobPersonnelRoles(),
				this.fetchUsers()
			]

			Promise.all(promises).then(function(responses){
				vm.spinner(false)

				vm.$store.payBoostOptions = vm.optionify(responses[0].data,"description")
			})
		}
	},
	created() {
		this.fetchData()
		this.setSettings()
	}
}
</script>